import * as React from "react";
import { Box, Button } from "@mui/material";

import { Controller, useForm } from "react-hook-form";
import { ReportProgress } from "../../types/project";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";
import SelectReason from "../../components/SelectReason";
import { useRejectTalentWorkMutation } from "../../services/projectJob.service";
import BaseModal from "../../components/global/BaseModal";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalRejectJobProps {
  open: boolean;
  onClose: () => void;
  progress: ReportProgress | null;
  projectId: number | null;
}

const ModalRejectJob = ({
  open,
  onClose,
  progress,
  projectId,
}: ModalRejectJobProps) => {
  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const [doReject, { isLoading }] = useRejectTalentWorkMutation();

  // UI Labels
  const professionalsWorkReviewLabel = useUILabel("project.professionalsWorkReview");
  const selectReviewReasonLabel = useUILabel("project.selectReviewReason");
  const workRejectedLabel = useUILabel("project.workRejected");
  const cancelLabel = useUILabel("common.cancel");
  const submitLabel = useUILabel("common.submit");
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");

  const onSubmit = async (payload: any) => {
    try {
      if (!progress || !projectId) return;
      await doReject({
        projectId,
        progressId: progress.id,
        reason: payload.reason,
      }).unwrap();
      toast.success(workRejectedLabel);
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("reason", "");
      clearErrors();
    }
  }, [progress, open]);

  const footer = (
    <>
      <Button onClick={onClose} disabled={isLoading}>
        {cancelLabel}
      </Button>
      <LoadingButton type="submit" variant="contained" loading={isLoading}>
        {submitLabel}
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={professionalsWorkReviewLabel}
      footer={footer}
      maxWidth="xs"
    >
      <Box 
        component="form" 
        onSubmit={handleSubmit(onSubmit)} 
        sx={{ 
          p: 3,
          "@media (orientation: landscape) and (max-width: 900px)": {
            p: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Controller
          name="reason"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <SelectReason
              label={selectReviewReasonLabel}
              onChange={onChange}
              error={!!errors.reason}
              helperText={!!errors.reason && thisFieldIsRequiredLabel}
              type="reject-work"
            />
          )}
        />
      </Box>
    </BaseModal>
  );
};
export default ModalRejectJob;
